<template>
  <div class='w-full h-full bg-white flex'>
    <div class='w-7/12 px-8'>
      <settings-item-header :title="'Bots'"
                            :description="'Manage your bots here: create new ones, update existing ones, or get information.'"
                            :is-adding-new-item="isAddingNewBot"
                            class="border-none"
                            @click="isAddingNewBot ? cancelAddNewBot() : addNewBot()"
      />
      <with-loader id="settings-bots-table-loader" :loading="loaders.bots" class="h-full pb-10" use-as-overlay>
        <div v-if="isEmpty(bots)"
             class="h-3/5 flex flex-col justify-center items-center">
          <img :src="noBotsImage" alt="no bots image">
          <span class="text-center text-gray-dark mt-8">
            To start with Amio, just create a bot.
          </span>
        </div>
        <settings-bots-list v-else-if="!loaders.bots"
                            :bots="bots"
                            :selected-bot-index="selectedBotIndex"
                            :selected-channel="selectedBotChannel"
                            :is-rename-mode="isRenameMode"
                            @update:bot-name="saveBotName"
                            @request-delete="handleRequestDelete"
                            @select-bot-index="selectBotIndex"
                            @select-channel-index="selectChannelIndex"
                            class="h-5/6"
        />
      </with-loader>
    </div>

    <transition name="slide-fade" mode="out-in">
      <SettingsBotSummary v-if="selectedBot && !selectedBotChannel"
                            :bot="selectedBot"
                            :key="selectedBot.id"
                            :is-rename-mode="isRenameMode"
                            :is-loading="loaders.name"
                            @set-rename-mode="setRenameMode"
                            @rename="saveBotName"
                            @request-delete="handleRequestDelete"
                            @update-bot="updateBot"
                            class="w-5/12"
      />
      <settings-bot-editor v-else-if="selectedBot"
                           :bot="selectedBot"
                           :key="selectedBot.id"
                           :is-rename-mode="isRenameMode"
                           :is-loading="loaders.name"
                           class="w-5/12"
                           :selected-channel="selectedBotChannel"
                           @set-rename-mode="setRenameMode"
                           @rename="saveBotName"
                           @request-delete="handleRequestDelete"
      />
      <settings-bot-create v-else-if="isAddingNewBot"
                           @process-new-bot="processNewBot"
                           class="w-5/12"
      />
    </transition>

    <delete-dialog v-if="isDeleteDialogShown"
                   :is-shown="isDeleteDialogShown"
                   :item="botToDelete"
                   :dialog-type="'prompt'"
                   :delete-title="'Delete Bot?'"
                   :delete-description="deleteDescription"
                   :delete-confirm-text="'Delete Bot'"
                   :input-placeholder="'Enter text'"
                   :required-prompt-value="requiredPromptValue"
                   @cancel="cancelDelete"
                   @delete="deleteBot"
    >
      <template #delete-item-icon>
        <delete-bot-icon class="h-8 w-8 text-gray-dark"/>
      </template>
    </delete-dialog>
  </div>
</template>

<script>
import DeleteBotIcon from '@/assets/images/icons/DeleteBotIcon'
import DeleteDialog from '@/components/common/DeleteDialog.vue'
import SettingsItemHeader from '@/components/settings/SettingsItemHeader.vue'
import SettingsBotsList from '@/components/settings/bots/SettingsBotsList.vue'
import SettingsBotSummary from '@/components/settings/bots/SettingsBotSummary.vue'
import SettingsBotEditor from '@/components/settings/bots/SettingsBotEditor.vue'
import SettingsBotCreate from '@/components/settings/bots/SettingsBotCreate.vue'
import WithLoader from '@/components/common/WithLoader.vue'
import botService from '@/services/amio/bot.service'
import channelService from '@/services/amio/channel.service'
import botModule from '@/store/modules/bot.module'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import findIndex from 'ramda/src/findIndex'
import isEmpty from 'ramda/src/isEmpty'
import prepend from 'ramda/src/prepend'
import propEq from 'ramda/src/propEq'
import update from 'ramda/src/update'
import toaster from '@/utils/toaster'
import { ROUTE_NAMES } from '@/router'
import { ITEM_IDS } from '@/utils/editor.constants'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import noBotsImage from '@/assets/images/bots_empty.svg'

export default {
  name: 'SettingsBots',
  components: {
    DeleteBotIcon,
    DeleteDialog,
    SettingsItemHeader,
    SettingsBotsList,
    SettingsBotSummary,
    SettingsBotEditor,
    SettingsBotCreate,
    WithLoader
  },

  watch: {
    '$route.params': {
      immediate: true,
      handler(params) {
        if (params.botId === ITEM_IDS.NEW) {
          this.selectedBotIndex = -1
        } else {
          this.selectBotIndexByRoute()
        }
      }
    }
  },

  data() {
    return {
      noBotsImage,
      selectedBotIndex: -1,
      selectedChannelIndex: -1,
      isRenameMode: false,
      loaders: {
        bots: false,
        name: false
      },
      botToDelete: null,
      isDeleteDialogShown: false,
      isEmpty
    }
  },

  computed: {
    bots: botModule.getBotList,

    deleteDescription() {
      return this.botToDelete ? `This will delete all data for "${this.botToDelete.name}". It cannot be undone.<br/><br/>Please type <strong>${this.botToDelete.name}</strong> to enable delete.` : ''
    },

    requiredPromptValue() {
      return this.botToDelete ? this.botToDelete.name : null
    },

    selectedBot() {
      return isEmpty(this.bots) && this.selectedBotIndex >= 0 ? null : this.bots[this.selectedBotIndex]
    },

    selectedBotChannel() {
      if (!this.selectedBot || this.selectedChannelIndex === -1) return null

      return this.selectedBot?.channels[this.selectedChannelIndex]
    },

    isAddingNewBot() {
      return this.$route.path.includes(ITEM_IDS.NEW)
    }
  },

  methods: {
    addNewBot() {
      this.selectedBotIndex = -1
      this.selectedChannelIndex = -1
      this.$router.push({ name: ROUTE_NAMES.bots, params: { botId: ITEM_IDS.NEW } })
    },

    cancelAddNewBot() {
      if (!isEmpty(this.bots)) {
        this.$router.push({
          name: ROUTE_NAMES.bots,
          params: {
            botId: this.bots[0].id
          }
        })
      } else {
        this.$router.push({
          name: ROUTE_NAMES.bots
        })
      }
    },

    selectBotIndex(botIndex) {
      if (this.selectedBotIndex === botIndex) return
      this.selectedBotIndex = botIndex
      this.selectedChannelIndex = -1
    },

    selectChannelIndex(channelIndex) {
      this.selectedChannelIndex = channelIndex
    },

    isBotsRoute() {
      return this.$route.name === ROUTE_NAMES.bots || this.$route.name === ROUTE_NAMES.botsWithChannel
    },

    selectBotIndexByRoute() {
      if (isEmpty(this.bots)) return
      if (!this.isBotsRoute()) return

      const routeBotId = this.$route.params.botId
      const routeChannelId = this.$route.params.channelId

      if (routeBotId) {
        this.selectedBotIndex = this.bots.findIndex(bot => bot.id === routeBotId)

        if (this.selectedBotIndex >= 0) {
          const bot = this.bots[this.selectedBotIndex]

          if (routeChannelId) {
            this.selectedChannelIndex = bot.channels.findIndex(channel => channel.id === routeChannelId)
          } else {
            this.selectedChannelIndex = -1
          }
        }
        return
      }

      this.$router.replace({
        name: ROUTE_NAMES.bots,
        params: {
          botId: this.bots[0].id
        }
      })
      this.selectedBotIndex = 0
      this.selectedChannelIndex = -1
    },

    setRenameMode(isRenameMode) {
      this.isRenameMode = isRenameMode
    },

    handleRequestDelete(bot) {
      this.botToDelete = bot
      this.isDeleteDialogShown = true
    },

    cancelDelete() {
      this.botToDelete = null
      this.isDeleteDialogShown = false
    },

    async deleteBot() {
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, true)
      try {
        await botService.delete(this.botToDelete.id)
        const updatedBotList = this.bots.filter(bot => bot.id !== this.botToDelete.id)
        await botModule.setBotList(updatedBotList)
        if (isEmpty(updatedBotList)) {
          this.addNewBot()
        } else {
          const selectedBot = this.bots[0]
          botModule.setBot(selectedBot)
          await this.$router.replace({
            name: ROUTE_NAMES.bots,
            params: {
              botId: selectedBot.id
            }
          })
          this.selectedBotIndex = 0
        }
      } catch (e) {
        toaster.error(e)
      } finally {
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, false)
        this.botToDelete = null
        this.isDeleteDialogShown = false
      }
    },

    async processNewBot(newBot) {
      newBot.channels = await channelService.channelList(newBot.id)
      await botModule.setBotList(prepend(newBot, this.bots))
      botModule.setBot(newBot)
      await this.$router.replace({
        name: ROUTE_NAMES.bots,
        params: {
          botId: newBot.id
        }
      })
    },

    async saveBotName(updatedBot) {
      try {
        this.loaders.name = true
        await botService.update(updatedBot.id, { name: updatedBot.name })
        await this.updateBot(updatedBot)
      } catch (err) {
        toaster.error(err)
      } finally {
        this.setRenameMode(false)
        this.loaders.name = false
      }
    },

    async updateBot(updatedBot) {
      const index = findIndex(propEq('id', updatedBot.id))(this.bots)
      await botModule.setBotList(update(index, updatedBot, this.bots))
    }
  },

  async mounted() {
    this.loaders.bots = true
    const bots = await botService.listBots()
    if (isEmpty(bots)) {
      this.loaders.bots = false
      this.addNewBot()
      return
    }

    try {
      const channelPromises = bots.map(bot => channelService.channelList(bot.id))
      const channels = await Promise.all(channelPromises)
      for (let i = 0; i < bots.length; i++) {
        bots[i].channels = channels[i]
      }
    } catch (err) {
      toaster.error(err)
    } finally {
      bots.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      await botModule.setBotList(bots)
      if (!this.$route.path.includes(ITEM_IDS.NEW)) this.selectBotIndexByRoute()
      this.loaders.bots = false
    }
  }
}
</script>

<style scoped>
.slide-fade-enter-active {
  transition: all .3s ease;
}

.slide-fade-leave-active {
  transition: all .3s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.slide-fade-enter, .slide-fade-leave-to {
  transform: translateX(10px);
  opacity: 0;
}
</style>
